import { DocumentNode } from 'graphql';
import { Tag, Trash } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GetCouponQuery as GetCoupon, GetCouponsQuery as GetCoupons, PurchaseStatus } from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../../common/Common/OptionsDropdown';
import { encodeUrl } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ActivateCouponMutation from './ActivateCouponMutation';
import CouponStatusIndicator from './CouponStatusIndicator';
import DeactivateCouponMutation from './DeactivateCouponMutation';
import DeleteCouponMutation from './DeleteCouponMutation';
import DiscountValue from '../../../../common/Common/DiscountValue';
import EditCouponForm from './EditCouponForm';
import GetCouponsQuery from './GetCouponsQuery';
import UI from '../../../../common/UI';
import useConfirmModal from '../../../../common/useConfirmModal';
import useLocale from '../../../../common/useLocale';
import useMediaDevice from '../../../../common/useMediaDevice';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../../common/useProject';

type Coupon = GetCoupons['project']['event']['coupons'][0];
type Event = GetCoupons['project']['event'];

interface CouponListProps {
  event: Event;
  onResetFilters: () => void;
  activeCouponId: string;
  couponData?: GetCoupon;
  refetchQueries?: DocumentNode[];
}

const CouponList = ({
  event, onResetFilters, activeCouponId, couponData, refetchQueries = [GetCouponsQuery],
}: CouponListProps) => {
  const project = useProject();
  const { t } = useTranslation();

  const hasFilteredCoupons = event.coupons.length > 0;

  return (
    <UI.Div>
      {hasFilteredCoupons ? (
        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {event.coupons.map((coupon, index) => (
            <UI.CollapsibleNavCard
              id={`CouponCard_${coupon.id}`}
              key={index}
              to={`/${project.id}/events/${event.id}/coupons/${activeCouponId !== coupon.id ? coupon.id : ''}`}
              open={coupon.id === activeCouponId}
              muted={!!activeCouponId && coupon.id !== activeCouponId}
              preview={(
                <CouponPreview
                  coupon={coupon}
                  event={event}
                  refetchQueries={refetchQueries}
                />
              )}
              body={couponData
                ? <EditCouponForm coupon={couponData.coupon} event={couponData.event} />
                : <UI.PageLoader />}
            />
          ))}
        </UI.GridContainer>
      ) : (
        <UI.Card
          sc={{ padding: 8, textAlign: 'center' }}
        >
          <UI.Div sc={{ color: 'secondary', mb: 2 }}>
            <Tag strokeWidth={0.5} size={60} />
          </UI.Div>
          <UI.Span>{t('no_coupons_match_filters')}</UI.Span>
          {' '}
          <UI.A onClick={onResetFilters}>{t('reset_filters')}</UI.A>
        </UI.Card>
      )}
    </UI.Div>
  );
};

interface CouponPreviewProps {
  coupon: Coupon;
  event: Event;
  refetchQueries?: DocumentNode[];
}

const CouponPreview = ({ coupon, event, refetchQueries }: CouponPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const device = useMediaDevice();
  const notifier = useNotifier();
  const history = useHistory();
  const project = useProject();

  const [activateCoupon] = useMutation(ActivateCouponMutation, {
    variables: {
      id: coupon.id,
    },
    optimisticResponse: {
      activateCoupon: {
        id: coupon.id,
        active: true,
        __typename: 'Coupon',
      },
    },
    onCompleted: () => notifier.success(t('notifications.coupon_activated')),
  });

  const [deactivateCoupon] = useMutation(DeactivateCouponMutation, {
    variables: {
      id: coupon.id,
    },
    optimisticResponse: {
      deactivateCoupon: {
        id: coupon.id,
        active: false,
        __typename: 'Coupon',
      },
    },
    onCompleted: () => notifier.success(t('notifications.coupon_deactivated')),
  });

  const toggleStatus = () => {
    if (coupon.active) {
      deactivateCoupon();
    } else {
      activateCoupon();
    }
  };

  const [deleteCoupon, { loading: deleting }] = useMutation(
    DeleteCouponMutation, {
      variables: {
        id: coupon.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.coupon_deleted'));

        history.push(`/${project.id}/events/${event.id}/coupons`);
      },
      onError: () => notifier.error(t('something_went_wrong')),
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: coupon.prefix }),
    onConfirm: () => deleteCoupon(),
  }, [coupon]);

  const muted = !coupon.active;

  return (
    <ContainerWithOptions
      name={coupon.prefix}
      renderOptions={() => (
        <Option
          icon={<Trash />}
          label={t('delete')}
          onClick={() => confirmDelete()}
          disabled={!coupon.deletable || deleting}
        />
      )}
    >
      <UI.GridContainer sc={{ columns: ['1fr 1fr', '3fr 1fr 1fr 1fr'], gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div sc={{ muted }}>
          <UI.Div>
            <UI.Label>
              <UI.Strong>{coupon.prefix}</UI.Strong>
              {coupon.codes_count > 0 && (
                <UI.Span>
                  &middot;&middot;&middot;&middot;&middot;&middot;&middot;&middot;
                </UI.Span>
              )}
            </UI.Label>
          </UI.Div>
          <DiscountValue discount={coupon} />
        </UI.Div>

        {!device.isSmall && (
          <UI.Div sc={{ muted }} style={{ gridColumn: '2 / span 2' }}>
            <UI.Div sc={{ fontWeight: 500 }}>
              {formatInteger(coupon.uses)}
              {coupon.max_uses !== null && (
                <UI.Sup sc={{ muted: true }}>
                  {` / ${formatInteger(coupon.max_uses)}`}
                </UI.Sup>
              )}
            </UI.Div>
            <UI.Link
              to={`/${project.id}/participants?${encodeUrl({
                event: event.id,
                coupons: [coupon.id],
                statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
              })}`}
              sc={{ secondary: true }}
            >
              {t('times_used', { count: coupon.uses })}
            </UI.Link>
          </UI.Div>
        )}

        <UI.Div sc={{ textAlign: 'right', small: true }}>
          <UI.Toggle checked={coupon.active} onChange={() => toggleStatus()} />

          <UI.Truncate sc={{ color: 'gray.500' }}>
            <CouponStatusIndicator coupon={coupon} />
          </UI.Truncate>
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

export default CouponList;
